/**
 * ArtifactModal — Phase 6 (refined v2)
 *
 * Two-step modal:
 *   Step 1 (config)    — Framework + Design Pattern selectors + "Generate" button
 *   Step 2 (artifact)  — Rendered Markdown output + "Copy to Clipboard" CTA
 *
 * Markdown is rendered via react-markdown + remark-gfm with custom components:
 *   - pre/code  → macOS-style dark IDE block (always dark, red/yellow/green dots)
 *   - inline code → violet-tinted pill
 *   - tables    → striped, bordered
 *
 * Opening modes:
 *   - Normal:  no initialMarkdown → starts at 'config', calls /generate
 *   - History: initialMarkdown provided → starts at 'done', no LLM call
 */

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import axios from 'axios';
import {
  X, Wand2, Loader2, Copy, Check, AlertTriangle,
  ChevronDown, History,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ── Constants ──────────────────────────────────────────────────────────────────

const FRAMEWORKS = [
  { value: 'playwright',  label: 'Playwright (TypeScript)' },
  { value: 'cypress',     label: 'Cypress (JavaScript)' },
  { value: 'pytest',      label: 'Pytest (Python)' },
  { value: 'vitest',      label: 'Vitest (TypeScript)' },
  { value: 'jest',        label: 'Jest (JavaScript)' },
  { value: 'webdriverio', label: 'WebdriverIO (TypeScript)' },
];

const DESIGN_PATTERNS = [
  { value: 'page-object',  label: 'Page Object Model' },
  { value: 'screenplay',   label: 'Screenplay Pattern' },
  { value: 'fixture',      label: 'Fixture-Based' },
  { value: 'fluent',       label: 'Fluent Interface' },
  { value: 'data-driven',  label: 'Data-Driven Testing' },
];

// ── Props ──────────────────────────────────────────────────────────────────────

export interface ArtifactModalProps {
  projectId: string;
  projectName: string;
  selectedIds: string[];
  onClose: () => void;
  /** Pre-existing markdown → opens directly in 'done' step (history mode) */
  initialMarkdown?: string;
  /** Called after a plan is successfully generated (not fired for history loads) */
  onGenerated?: () => void;
  /** Pass the planId to upgrade a Draft to Completed */
  planId?: string;
}

// ── IDE Code Block ─────────────────────────────────────────────────────────────
// Always dark, regardless of app light/dark mode. Mimics VS Code / macOS terminal.

function IDECodeBlock({ lang, children }: { lang: string; children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-lg overflow-hidden border border-slate-600 shadow-lg">
      {/* Title bar */}
      <div className="flex items-center gap-1.5 px-3 py-2 bg-[#1e1e2e] border-b border-slate-600/70">
        {/* Traffic-light dots */}
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <span className="ml-3 text-[11px] font-mono text-slate-400 select-none">
          {lang || 'code'}
        </span>
      </div>
      {/* Code body */}
      <pre className="overflow-x-auto px-4 py-4 bg-[#13131f] text-slate-200 text-[12.5px] font-mono leading-relaxed m-0 whitespace-pre">
        {children}
      </pre>
    </div>
  );
}

// ── react-markdown custom components ──────────────────────────────────────────

const mdComponents: Components = {
  // Block code: custom pre wrapper extracts language from child <code className="language-xxx">
  pre({ children }) {
    let lang = '';
    // Walk children to find the <code> element and extract its language class
    const child = Array.isArray(children) ? children[0] : children;
    if (child && typeof child === 'object' && 'props' in (child as object)) {
      const cn: string = (child as any).props?.className ?? '';
      lang = /language-(\w+)/.exec(cn)?.[1] ?? '';
    }
    return <IDECodeBlock lang={lang}>{children}</IDECodeBlock>;
  },

  // Code: inline code gets pill styling; block code (inside pre) passes through cleanly
  code({ className, children }) {
    const isBlock = Boolean(className); // fenced blocks always have "language-xxx"
    if (isBlock) {
      // Inside our custom <pre> — just render the raw content, pre handles the chrome
      return <code className={className}>{children}</code>;
    }
    // Inline code
    return (
      <code className="px-1.5 py-0.5 rounded text-[11.5px] font-mono bg-slate-100 dark:bg-slate-800 text-violet-700 dark:text-violet-300 border border-slate-200 dark:border-slate-700 leading-normal">
        {children}
      </code>
    );
  },

  // Headings
  h1({ children }) {
    return (
      <h1 className="text-xl font-bold text-slate-900 dark:text-white mt-7 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
        {children}
      </h1>
    );
  },
  h2({ children }) {
    return (
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 mt-6 mb-2">
        {children}
      </h2>
    );
  },
  h3({ children }) {
    return (
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-4 mb-1.5">
        {children}
      </h3>
    );
  },

  // Paragraphs
  p({ children }) {
    return (
      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed my-2">
        {children}
      </p>
    );
  },

  // Lists
  ul({ children }) {
    return (
      <ul className="my-2 ml-5 space-y-1 list-disc list-outside text-sm text-slate-700 dark:text-slate-300">
        {children}
      </ul>
    );
  },
  ol({ children }) {
    return (
      <ol className="my-2 ml-5 space-y-1 list-decimal list-outside text-sm text-slate-700 dark:text-slate-300">
        {children}
      </ol>
    );
  },
  li({ children }) {
    return <li className="leading-relaxed">{children}</li>;
  },

  // Horizontal rule
  hr() {
    return <hr className="my-5 border-slate-200 dark:border-slate-700" />;
  },

  // Blockquote
  blockquote({ children }) {
    return (
      <blockquote className="my-3 pl-4 border-l-4 border-violet-400 dark:border-violet-600 text-sm text-slate-600 dark:text-slate-400 italic">
        {children}
      </blockquote>
    );
  },

  // Strong / em
  strong({ children }) {
    return (
      <strong className="font-semibold text-slate-900 dark:text-slate-100">{children}</strong>
    );
  },
  em({ children }) {
    return <em className="italic text-slate-600 dark:text-slate-400">{children}</em>;
  },

  // Tables (remark-gfm)
  table({ children }) {
    return (
      <div className="my-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs border-collapse">{children}</table>
      </div>
    );
  },
  thead({ children }) {
    return <thead className="bg-slate-100 dark:bg-slate-800">{children}</thead>;
  },
  tbody({ children }) {
    return <tbody className="divide-y divide-slate-200 dark:divide-slate-700">{children}</tbody>;
  },
  tr({ children }) {
    return (
      <tr className="even:bg-slate-50 dark:even:bg-slate-800/40">{children}</tr>
    );
  },
  th({ children }) {
    return (
      <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="px-3 py-2 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
        {children}
      </td>
    );
  },
};

// ── Select helper ──────────────────────────────────────────────────────────────

function StyledSelect({
  label, value, onChange, options, disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none pl-3 pr-8 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ArtifactModal({
  projectId,
  projectName,
  selectedIds,
  onClose,
  initialMarkdown,
  onGenerated,
  planId,
}: ArtifactModalProps) {
  const { token } = useAuth();

  const isHistoryMode = Boolean(initialMarkdown);

  const [step, setStep]               = useState<'config' | 'generating' | 'done' | 'error'>(
    isHistoryMode ? 'done' : 'config',
  );
  const [framework, setFramework]     = useState('playwright');
  const [designPattern, setDesignPattern] = useState('page-object');
  const [markdown, setMarkdown]       = useState(initialMarkdown ?? '');
  const [errorMsg, setErrorMsg]       = useState<string | null>(null);
  const [copied, setCopied]           = useState(false);

  // Sync when parent swaps the history plan while modal is already open
  useEffect(() => {
    if (initialMarkdown) {
      setMarkdown(initialMarkdown);
      setStep('done');
    }
  }, [initialMarkdown]);

  // ── Generate ────────────────────────────────────────────────────────────────

  async function handleGenerate() {
    setStep('generating');
    setErrorMsg(null);

    try {
      const res = await axios.post(
        `${API_URL}/api/ai/automation-planner/generate`,
        { projectId, selectedTestIds: selectedIds, framework, designPattern, planId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!res.data.success) {
        throw new Error(res.data.error ?? 'Unknown error from server.');
      }

      setMarkdown(res.data.data.markdown as string);
      setStep('done');
      onGenerated?.();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : (err instanceof Error ? err.message : 'Unexpected error.');
      setErrorMsg(msg);
      setStep('error');
    }
  }

  // ── Copy to clipboard ────────────────────────────────────────────────────────

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard API unavailable — silently fail
    }
  }, [markdown]);

  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const modal = (
    <div
      role="button"
      tabIndex={-1}
      aria-label="Close modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 cursor-default"
      onClick={handleBackdrop}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div
        className="relative w-full max-w-3xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-violet-100 dark:bg-violet-900/40">
              {isHistoryMode
                ? <History size={14} className="text-violet-600 dark:text-violet-400" />
                : <Wand2 size={14} className="text-violet-600 dark:text-violet-400" />
              }
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                {isHistoryMode ? 'Saved Automation Plan' : 'Automation Strategy Artifact'}
              </h2>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {projectName} · {selectedIds.length} test{selectedIds.length !== 1 ? 's' : ''}
                {isHistoryMode && <span className="ml-1 text-violet-500">(from history)</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {step === 'done' && (
              <button
                onClick={handleCopy}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  copied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-900/50'
                }`}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* CONFIG step */}
          {step === 'config' && (
            <div className="px-6 py-6 flex flex-col gap-6">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Choose the target framework and design pattern. The AI will generate a complete strategy document
                and a copy-pasteable master prompt for Cursor / GitHub Copilot.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StyledSelect
                  label="Automation Framework"
                  value={framework}
                  onChange={setFramework}
                  options={FRAMEWORKS}
                />
                <StyledSelect
                  label="Design Pattern"
                  value={designPattern}
                  onChange={setDesignPattern}
                  options={DESIGN_PATTERNS}
                />
              </div>

              <div className="px-4 py-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/40 text-sm text-violet-800 dark:text-violet-300">
                <strong>{selectedIds.length}</strong> test{selectedIds.length !== 1 ? 's' : ''} will be included in the strategy document.
              </div>
            </div>
          )}

          {/* GENERATING step */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
              <Loader2 size={36} className="animate-spin text-violet-500" />
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Generating strategy artifact…
              </p>
              <p className="text-xs text-slate-400 text-center max-w-xs">
                The AI is analysing your test cases and composing the full strategy document.
                This may take 15–30 seconds.
              </p>
            </div>
          )}

          {/* ERROR step */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center gap-4 py-16 px-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20">
                <AlertTriangle size={22} className="text-red-500" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-800 dark:text-white mb-1">Generation failed</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{errorMsg}</p>
              </div>
              <button
                onClick={() => setStep('config')}
                className="text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
              >
                Back to settings
              </button>
            </div>
          )}

          {/* DONE step — react-markdown rendered output */}
          {step === 'done' && (
            <div className="px-6 py-5">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {markdown}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>

          {step === 'config' && (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            >
              <Wand2 size={14} />
              Generate Artifact
            </button>
          )}

          {step === 'done' && (
            <button
              onClick={handleCopy}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                copied ? 'bg-green-600 text-white' : 'bg-violet-600 text-white hover:bg-violet-700'
              }`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
